// ui/hooks/useApprovalFlow.ts
import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebaseClient";
import type { ApprovalFlow, ApprovalModule } from "../types/approval";

function normalizeApprovalModule(module: string): string {
  const m = String(module || "").trim();
  if (!m) return m;

  // legacy strings
  if (m === "sealRequest") return "SEAL_REQUEST";
  if (m === "contractDraft") return "CONTRACT_REVIEW";
  if (m === "contractReview") return "CONTRACT_REVIEW";
  if (m === "officialReply") return "OFFICIAL_REPLY";

  return m;
}

/**
 * Reads approvalFlows by trying multiple possible docIds:
 * - canonical: `${CANONICAL_MODULE}_${targetId}`
 * - legacy: `${ORIGINAL_MODULE}_${targetId}` (when different)
 * - overrideDocId: exact doc id (highest priority)
 */
export function useApprovalFlow(
  module: ApprovalModule | string,
  targetId: string,
  overrideDocId?: string
) {
  const [flow, setFlow] = useState<ApprovalFlow | null>(null);
  const [loading, setLoading] = useState(false);

  const candidates = useMemo(() => {
    if (!module || !targetId) return [];
    if (overrideDocId) return [overrideDocId];

    const original = String(module).trim();
    const canonical = normalizeApprovalModule(original);

    const ids = [`${canonical}_${targetId}`];
    if (original && original !== canonical) ids.push(`${original}_${targetId}`);

    return ids;
  }, [module, targetId, overrideDocId]);

  useEffect(() => {
    if (!targetId || !module) {
      setFlow(null);
      return;
    }

    if (!candidates.length) {
      setFlow(null);
      return;
    }

    let unsub: null | (() => void) = null;
    let idx = 0;
    let disposed = false;

    setLoading(true);

    const listen = (docId: string) => {
      unsub = (db as any)
        .collection("approvalFlows")
        .doc(docId)
        .onSnapshot(
          (docSnap: any) => {
            if (disposed) return;

            // found
            if (docSnap.exists) {
              setFlow(docSnap.data() as ApprovalFlow);
              setLoading(false);
              return;
            }

            // try next candidate
            if (idx < candidates.length - 1) {
              if (unsub) unsub();
              idx += 1;
              listen(candidates[idx]);
              return;
            }

            // none found
            setFlow(null);
            setLoading(false);
          },
          (err: any) => {
            if (disposed) return;
            console.error("Approval Flow Listener Error", err);
            setFlow(null);
            setLoading(false);
          }
        );
    };

    listen(candidates[idx]);

    return () => {
      disposed = true;
      if (unsub) unsub();
    };
  }, [module, targetId, candidates]);

  return { flow, loading };
}

